import { useController, useFormContext, useWatch } from 'react-hook-form';

import { Field, TextInput, useFieldControl } from '../../shared/form';
import { Segmented } from '../../shared/ui/segmented';
import type { ICredentialApp, TConnector } from '../../entities/credential';
import {
  formFields,
  httpShows,
  httpTokenHint,
  httpTokenLabel,
  type IFormField,
  type TCredentialValues,
} from './lib';

// Board 13 says "A header" where the catalog says "Custom header".
const BOARD_OPTION: Record<string, string> = {
  bearer: 'Bearer',
  basic: 'Basic',
  header: 'A header',
};

const SegmentedControl = ({ field }: { field: IFormField }) => {
  const { name } = useFieldControl();
  const { control } = useFormContext<TCredentialValues>();
  const { field: bound } = useController({ name, control });

  return (
    <Segmented
      fill
      aria-label={field.label}
      value={bound.value ?? ''}
      onChange={bound.onChange}
      segments={(field.field.options ?? []).map((option) => ({
        value: option.value,
        label: BOARD_OPTION[option.value] ?? option.label,
      }))}
    />
  );
};

const Control = ({ field }: { field: IFormField }) => {
  if (field.field.kind === 'select') return <SegmentedControl field={field} />;
  const secret = field.field.kind === 'secret';
  return (
    <TextInput
      mono
      type={secret ? 'password' : 'text'}
      inputMode={field.field.kind === 'number' ? 'numeric' : undefined}
      autoComplete={secret ? 'new-password' : 'off'}
      spellCheck={false}
      placeholder={field.field.placeholder}
    />
  );
};

const OneField = ({ field, app }: { field: IFormField; app: ICredentialApp }) => {
  const scheme = useWatch<TCredentialValues>({ name: 'scheme' });
  const header = useWatch<TCredentialValues>({ name: 'headerName' });
  const http = app.app === 'http';
  if (http && !httpShows(scheme, field.path)) return null;

  const label = http && field.path === 'token' ? httpTokenLabel(scheme) : field.label;
  const hint =
    http && field.path === 'token'
      ? httpTokenHint(scheme, header)
      : (field.hint ?? field.field.help);

  return (
    <Field<TCredentialValues> name={field.path} label={label} hint={hint}>
      <Control field={field} />
    </Field>
  );
};

interface ICredentialFieldsProps {
  app: ICredentialApp;
  connectors: readonly TConnector[];
}

/** Name first, then the app's own fields; SMTP's host and port share a line. */
export const CredentialFields = ({ app, connectors }: ICredentialFieldsProps) => {
  const fields = formFields(app, connectors);
  const port = fields.find((field) => field.path === 'port');

  return (
    <>
      <Field<TCredentialValues> name="name" label="Name">
        <TextInput autoFocus placeholder={app.placeholderName} autoComplete="off" />
      </Field>
      {fields.map((field) => {
        if (field.path === 'port') return null;
        if (field.path === 'host' && port) {
          return (
            <div key={field.path} className="flex items-start gap-3">
              <div className="min-w-0 flex-1">
                <OneField field={field} app={app} />
              </div>
              <div className="w-col-flow-num shrink-0">
                <OneField field={port} app={app} />
              </div>
            </div>
          );
        }
        return <OneField key={field.path} field={field} app={app} />;
      })}
    </>
  );
};
